// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import Accessibility from '@app/features/accessibility/state/Accessibility';
import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {
	requestCopyMessageId,
	requestCopyMessageLink,
	requestCopyMessageText,
	requestDeleteMessage,
	requestMarkMessageUnread,
	requestMessageForward,
	requestMessagePin,
	requestMessageReply,
	requestSpeakMessage,
	requestToggleBookmark,
	requestToggleSuppressEmbeds,
	startMessageEdit,
	triggerAddReaction,
} from '@app/features/channel/components/MessageActionUtils';
import {CreateDMModal} from '@app/features/channel/components/modals/CreateDMModal';
import type {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import * as VoiceStateCommands from '@app/features/devtools/commands/VoiceStateCommands';
import {AddGuildModal} from '@app/features/guild/components/modals/AddGuildModal';
import type {Guild} from '@app/features/guild/models/Guild';
import GuildList from '@app/features/guild/state/GuildList';
import Guilds from '@app/features/guild/state/Guilds';
import * as InboxCommands from '@app/features/inbox/commands/InboxCommands';
import Inbox from '@app/features/inbox/state/Inbox';
import Keybind, {
	type CustomKeybindEntry,
	type KeybindCommand,
	type KeybindConfig,
	type KeyCombo,
} from '@app/features/input/state/Keybind';
import {jsKeyToUiohookKeycode} from '@app/features/input/utils/UiohookKeycodes';
import type {Message} from '@app/features/messaging/models/Message';
import MessageFocus from '@app/features/messaging/state/MessageFocus';
import SavedMessages from '@app/features/messaging/state/SavedMessages';
import {openExternalUrlWithWarning} from '@app/features/messaging/utils/ExternalLinkUtils';
import {goToMessage} from '@app/features/messaging/utils/MessageNavigator';
import * as NavigationCommands from '@app/features/navigation/commands/NavigationCommands';
import Navigation from '@app/features/navigation/state/Navigation';
import SelectedChannel from '@app/features/navigation/state/SelectedChannel';
import SelectedGuild from '@app/features/navigation/state/SelectedGuild';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import MentionFeed from '@app/features/notification/state/MentionFeed';
import Permission from '@app/features/permissions/state/Permission';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {ComponentDispatch} from '@app/features/platform/utils/ComponentBus';
import * as ReadStateCommands from '@app/features/read_state/commands/ReadStateCommands';
import ReadStates from '@app/features/read_state/state/ReadStates';
import QuickSwitcher from '@app/features/search/state/QuickSwitcher';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import KeyboardMode from '@app/features/ui/state/KeyboardMode';
import {isFirefoxWebReservedZoomShortcut} from '@app/features/ui/utils/AppZoomKeybindUtils';
import {getElectronAPI, isNativeLinux, isNativeMacOS} from '@app/features/ui/utils/NativeUtils';
import {UserSettingsModal} from '@app/features/user/components/modals/UserSettingsModal';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import * as CallUtils from '@app/features/voice/utils/CallUtils';
import {checkNativePermission} from '@app/features/voice/utils/NativePermissions';
import {ME} from '@app/vendor/constants/AppConstants';
import {ChannelTypes, Permissions} from '@app/vendor/constants/ChannelConstants';
import {JumpTypes} from '@app/vendor/constants/JumpConstants';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import CombokeysImport from 'combokeys';
import {autorun, reaction} from 'mobx';
import React from 'react';

interface CombokeysInstance {
	bind(keys: string | Array<string>, callback: (event: KeyboardEvent) => void, action?: string): void;
	reset(): void;
	detach(): void;
	stopCallback: (e: Event, element: Element) => boolean;
}
type ShortcutSource = 'local' | 'global';
type KeybindHandler = (payload: {
	type: 'press' | 'release';
	source: ShortcutSource;
	context?: {
		focusedMessage?: Message;
	};
	shiftKey?: boolean;
}) => void;
type RuntimeKeybind = KeybindConfig & {
	combo: KeyCombo;
};
const NON_TEXT_INPUT_TYPES = new Set([
	'button',
	'checkbox',
	'radio',
	'range',
	'color',
	'file',
	'image',
	'submit',
	'reset',
]);
const isEditableElement = (target: EventTarget | null): target is HTMLElement => {
	if (!(target instanceof HTMLElement)) return false;
	if (target.isContentEditable) return true;
	const tagName = target.tagName;
	if (tagName === 'TEXTAREA') return true;
	if (tagName === 'INPUT') {
		const type = ((target as HTMLInputElement).type || '').toLowerCase();
		return !NON_TEXT_INPUT_TYPES.has(type);
	}
	return false;
};
const electronCodeKeyMap: Record<string, string> = {
	Space: 'Space',
	Spacebar: 'Space',
	Tab: 'Tab',
	CapsLock: 'Capslock',
	NumLock: 'Numlock',
	ScrollLock: 'Scrolllock',
	Backspace: 'Backspace',
	Delete: 'Delete',
	Insert: 'Insert',
	Enter: 'Enter',
	Return: 'Enter',
	ArrowUp: 'Up',
	ArrowDown: 'Down',
	ArrowLeft: 'Left',
	ArrowRight: 'Right',
	Home: 'Home',
	End: 'End',
	PageUp: 'PageUp',
	PageDown: 'PageDown',
	Escape: 'Escape',
	Esc: 'Escape',
	PrintScreen: 'PrintScreen',
	Pause: 'Pause',
	Numpad0: 'num0',
	Numpad1: 'num1',
	Numpad2: 'num2',
	Numpad3: 'num3',
	Numpad4: 'num4',
	Numpad5: 'num5',
	Numpad6: 'num6',
	Numpad7: 'num7',
	Numpad8: 'num8',
	Numpad9: 'num9',
	NumpadDecimal: 'numdec',
	NumpadAdd: 'numadd',
	NumpadSubtract: 'numsub',
	NumpadMultiply: 'nummult',
	NumpadDivide: 'numdiv',
	AudioVolumeUp: 'VolumeUp',
	AudioVolumeDown: 'VolumeDown',
	AudioVolumeMute: 'VolumeMute',
	MediaTrackNext: 'MediaNextTrack',
	MediaTrackPrevious: 'MediaPreviousTrack',
	MediaStop: 'MediaStop',
	MediaPlayPause: 'MediaPlayPause',
	Minus: '-',
	Equal: '=',
	Comma: ',',
	Period: '.',
	Semicolon: ';',
	Quote: "'",
	Slash: '/',
	Backslash: '\\',
	BracketLeft: '[',
	BracketRight: ']',
	Backquote: '`',
};
const ELECTRON_ACCELERATOR_KEYS = new Set<string>([
	'Space',
	'Tab',
	'Capslock',
	'Numlock',
	'Scrolllock',
	'Backspace',
	'Delete',
	'Insert',
	'Enter',
	'Up',
	'Down',
	'Left',
	'Right',
	'Home',
	'End',
	'PageUp',
	'PageDown',
	'Escape',
	'PrintScreen',
	'Pause',
	'num0',
	'num1',
	'num2',
	'num3',
	'num4',
	'num5',
	'num6',
	'num7',
	'num8',
	'num9',
	'numdec',
	'numadd',
	'numsub',
	'nummult',
	'numdiv',
	'VolumeUp',
	'VolumeDown',
	'VolumeMute',
	'MediaNextTrack',
	'MediaPreviousTrack',
	'MediaStop',
	'MediaPlayPause',
	'-',
	'=',
	',',
	'.',
	';',
	"'",
	'/',
	'\\',
	'[',
	']',
	'`',
	'+',
]);
const isElectronAcceleratorKey = (key: string): boolean => {
	if (ELECTRON_ACCELERATOR_KEYS.has(key)) return true;
	if (/^[A-Z0-9]$/.test(key)) return true;
	if (/^F([1-9]|1[0-9]|2[0-4])$/.test(key)) return true;
	return false;
};
const keyForElectronAccelerator = (combo: KeyCombo): string | null => {
	if (combo.mouseButton != null || combo.gamepadButton != null || combo.modifierOnly) return null;
	const code = combo.code;
	if (code) {
		const mapped = electronCodeKeyMap[code];
		if (mapped) return mapped;
		const keyCodeMatch = /^Key([A-Z])$/.exec(code);
		if (keyCodeMatch) return keyCodeMatch[1] ?? null;
		const digitCodeMatch = /^Digit([0-9])$/.exec(code);
		if (digitCodeMatch) return digitCodeMatch[1] ?? null;
		if (/^F([1-9]|1[0-9]|2[0-4])$/.test(code)) return code.toUpperCase();
	}
	const raw = combo.key || code;
	if (!raw) return null;
	if (raw === ' ') return 'Space';
	if (/^F([1-9]|1[0-9]|2[0-4])$/.test(raw)) return raw.toUpperCase();
	if (raw.length === 1) {
		const upper = raw.toUpperCase();
		return isElectronAcceleratorKey(upper) ? upper : null;
	}
	const mapped = electronCodeKeyMap[raw];
	if (mapped) return mapped;
	return null;
};
const comboToShortcutString = (combo: KeyCombo): string => {
	const parts: Array<string> = [];
	if (combo.ctrl) {
		parts.push('Control');
	} else if (combo.ctrlOrMeta) {
		parts.push('CommandOrControl');
	}
	if (combo.meta && !combo.ctrlOrMeta) parts.push('Super');
	if (combo.shift) parts.push('Shift');
	if (combo.alt) parts.push('Alt');
	const key = keyForElectronAccelerator(combo);
	if (!key) return '';
	parts.push(key);
	return parts.filter(Boolean).join('+');
};
const codeKeyForCombokeys: Record<string, string> = {
	Space: 'space',
	Spacebar: 'space',
	Escape: 'esc',
	Esc: 'esc',
	Enter: 'enter',
	Tab: 'tab',
	Backspace: 'backspace',
	ArrowUp: 'up',
	ArrowDown: 'down',
	ArrowLeft: 'left',
	ArrowRight: 'right',
	Slash: '/',
	Semicolon: ';',
	Period: '.',
	Comma: ',',
	Quote: "'",
	Minus: '-',
	Equal: '=',
	BracketLeft: '[',
	BracketRight: ']',
	Backquote: '`',
	Backslash: '\\',
};
const keyFromComboForCombokeys = (combo: KeyCombo, source: 'key' | 'code' = 'key'): string | null => {
	if (combo.modifierOnly) return null;
	const raw = source === 'code' ? combo.code : combo.key || combo.code;
	if (!raw) return null;
	if (raw === ' ') return 'space';
	if (raw.length === 1) {
		return raw.toLowerCase();
	}
	if (/^Key[A-Z]$/.test(raw)) {
		return raw.slice(3).toLowerCase();
	}
	if (/^Digit[0-9]$/.test(raw)) {
		return raw.slice(5);
	}
	return codeKeyForCombokeys[raw] ?? raw.toLowerCase();
};
const comboToCombokeysString = (combo: KeyCombo, source: 'key' | 'code' = 'key'): string | null => {
	const parts: Array<string> = [];
	if (combo.ctrl) {
		parts.push('ctrl');
	} else if (combo.ctrlOrMeta) {
		parts.push('mod');
	}
	if (combo.meta && !combo.ctrlOrMeta) parts.push('meta');
	if (combo.shift) parts.push('shift');
	if (combo.alt) parts.push('alt');
	const key = keyFromComboForCombokeys(combo, source);
	if (!key) return null;
	parts.push(key);
	return parts.join('+');
};
export const comboToCombokeysStrings = (combo: KeyCombo): Array<string> => {
	const shortcuts = [comboToCombokeysString(combo)];
	if (combo.code) {
		shortcuts.push(comboToCombokeysString(combo, 'code'));
	}
	return [...new Set(shortcuts.filter((shortcut): shortcut is string => !!shortcut))];
};
type HoldAction = 'voice_push_to_talk' | 'voice_push_to_talk_priority' | 'voice_push_to_mute' | 'voice_priority_vad';
const HOLD_ACTIONS: ReadonlyArray<HoldAction> = [
	'voice_push_to_talk',
	'voice_push_to_talk_priority',
	'voice_push_to_mute',
	'voice_priority_vad',
];
const HOLD_ACTIONS_FOR_PTT_MODE: ReadonlyArray<HoldAction> = ['voice_push_to_talk', 'voice_push_to_talk_priority'];
const HOLD_ACTIONS_FOR_VOICE_ACTIVITY_MODE: ReadonlyArray<HoldAction> = ['voice_push_to_mute', 'voice_priority_vad'];
function hasTriggerKey(combo: KeyCombo): boolean {
	return (combo.key ?? '') !== '' || (combo.code ?? '') !== '';
}
function hasTriggerInput(combo: KeyCombo): boolean {
	return hasTriggerKey(combo) || combo.mouseButton != null || combo.gamepadButton != null;
}
function isEnabledDefaultCombo(combo: KeyCombo): boolean {
	return (combo.enabled ?? true) !== false && hasTriggerInput(combo);
}
function getEnabledCustomActionOverrides(customs: ReadonlyArray<CustomKeybindEntry>): Set<KeybindCommand> {
	const overriddenActions = new Set<KeybindCommand>();
	for (const custom of customs) {
		if (!custom.action || !custom.enabled) continue;
		overriddenActions.add(custom.action);
	}
	return overriddenActions;
}
function buildDefaultRuntimeKeybinds(
	defaults: ReadonlyArray<KeybindConfig>,
	overriddenActions: Set<KeybindCommand>,
): Array<RuntimeKeybind> {
	const result: Array<RuntimeKeybind> = [];
	for (const entry of defaults) {
		if (overriddenActions.has(entry.action)) continue;
		const combo = entry.combo;
		if (!isEnabledDefaultCombo(combo)) continue;
		result.push({...entry, combo});
	}
	return result;
}
function buildCustomRuntimeKeybinds(customs: ReadonlyArray<CustomKeybindEntry>): Array<RuntimeKeybind> {
	const result: Array<RuntimeKeybind> = [];
	for (const custom of customs) {
		if (!custom.action || !custom.enabled) continue;
		const base = Keybind.getDefaultByAction(custom.action);
		if (!base) continue;
		const combo = custom.combo;
		if (!hasTriggerInput(combo)) continue;
		result.push({...base, combo});
	}
	return result;
}
function comboModifierSignature(combo: KeyCombo): string {
	return [
		combo.ctrlOrMeta ? 'mod' : '',
		combo.ctrl ? 'ctrl' : '',
		combo.alt ? 'alt' : '',
		combo.shift ? 'shift' : '',
		combo.meta ? 'meta' : '',
	]
		.filter(Boolean)
		.join('+');
}
function hookShortcutIdForKeybind(keybind: RuntimeKeybind): string | null {
	if (keybind.combo.mouseButton != null) {
		const modifiers = comboModifierSignature(keybind.combo);
		return `mouse:${keybind.action}:${modifiers}:${keybind.combo.mouseButton}`;
	}
	const shortcut = comboToShortcutString(keybind.combo);
	return shortcut || null;
}
interface HoldBindingRuntime {
	action: HoldAction;
	combo: KeyCombo;
	keycode: number | null;
	mouseButton: number | null;
	gamepadButton: number | null;
	isModifierOnly: boolean;
	ctrlOrMeta: boolean;
	requireBothSides: boolean;
	modifiers: {
		ctrl: boolean;
		alt: boolean;
		shift: boolean;
		meta: boolean;
	};
	routing: 'global' | 'local' | null;
	pressedKeycodes: Set<number>;
	localPressedCodes: Set<string>;
	localActiveCode: string | null;
	localMouseActive: boolean;
	localKeyDown: ((event: KeyboardEvent) => void) | null;
	localKeyUp: ((event: KeyboardEvent) => void) | null;
	localMouseDown: ((event: MouseEvent) => void) | null;
	localMouseUp: ((event: MouseEvent) => void) | null;
	gamepadHeld: boolean;
}
class KeybindManager {
	private handlers = new Map<KeybindCommand, KeybindHandler>();
	private initialized = false;
	private manualSuspendCount = 0;
	private routeSuspended = false;
	private disposers: Array<() => void> = [];
	private combokeys: CombokeysInstance | null = null;
	private accessibilityStatus: 'unknown' | 'granted' | 'denied' = 'unknown';
	private pttReleaseTimer: ReturnType<typeof setTimeout> | null = null;
	private globalShortcutUnsubscribe: (() => void) | null = null;
	private registeredGlobalShortcutIds = new Set<string>();
	private registeredPortalShortcutActionIds = new Set<KeybindCommand>();
	private hookFallbackShortcutIds = new Set<string>();
	private globalKeyHookUnsubscribes: Array<() => void> = [];
	private globalKeybindTriggeredUnsubscribe: (() => void) | null = null;
	private globalShortcutPortalBindingsUnsubscribe: (() => void) | null = null;
	private globalKeyHookStarted = false;
	private holdBindings: Array<HoldBindingRuntime> = [];
	private localHoldListenerAttached = false;
	private localMouseShortcutListenerAttached = false;
	private gamepadRafId: number | null = null;
	private gamepadListenersAttached = false;
	private gamepadShortcutStates = new Map<string, {binding: RuntimeKeybind; pressed: boolean}>();
	private logger = new Logger('KeybindManager');
	private get suspended(): boolean {
		return this.manualSuspendCount > 0 || this.routeSuspended;
	}
	private get currentChannelId(): string | null {
		return SelectedChannel.currentChannelId;
	}
	private get currentGuildId(): string | null {
		return SelectedGuild.selectedGuildId;
	}
	private navigateToChannel(guildId: string | null, channelId: string): void {
		const channel = Channels.getChannel(channelId);
		const effectiveGuildId = guildId ?? channel?.guildId ?? null;
		if (channel?.guildId) {
			NavigationCommands.selectChannel(channel.guildId, channelId);
			return;
		}
		if (channel && !channel.guildId) {
			NavigationCommands.selectChannel(ME, channelId);
			return;
		}
		if (effectiveGuildId) {
			NavigationCommands.selectChannel(effectiveGuildId, channelId);
		}
	}
	private get activeKeybinds(): Array<RuntimeKeybind> {
		const skipDefaults = Keybind.getDisableBuiltinKeybinds();
		const defaults = skipDefaults ? [] : Keybind.getDefaultsForRuntimeDispatch();
		const customs = Keybind.getCustomKeybinds();
		const overriddenActions = getEnabledCustomActionOverrides(customs);
		return [...buildDefaultRuntimeKeybinds(defaults, overriddenActions), ...buildCustomRuntimeKeybinds(customs)];
	}
	private get activeGlobalKeybinds(): Array<RuntimeKeybind> {
		return this.activeKeybinds.filter(
			(k) =>
				!HOLD_ACTIONS.includes(k.action as HoldAction) &&
				k.allowGlobal &&
				(k.combo.global ?? false) &&
				((k.combo.key ?? '') !== '' || (k.combo.code ?? '') !== ''),
		);
	}
	private get activeMouseShortcutKeybinds(): Array<RuntimeKeybind> {
		return this.activeKeybinds.filter(
			(k) => !HOLD_ACTIONS.includes(k.action as HoldAction) && k.combo.mouseButton != null,
		);
	}
	private get activeGamepadShortcutKeybinds(): Array<RuntimeKeybind> {
		return this.activeKeybinds.filter(
			(k) => !HOLD_ACTIONS.includes(k.action as HoldAction) && k.combo.gamepadButton != null,
		);
	}
	private get globalShortcutPortalEntries(): Array<{
		id: KeybindCommand;
		description: string;
		preferredTrigger?: string;
	}> {
		const activePreferredTriggers = new Map<KeybindCommand, string>();
		for (const keybind of this.activeKeybinds) {
			if (!keybind.allowGlobal || (keybind.combo.global ?? false) !== true) continue;
			const preferredTrigger = comboToShortcutString(keybind.combo);
			if (!preferredTrigger) continue;
			if (!activePreferredTriggers.has(keybind.action)) {
				activePreferredTriggers.set(keybind.action, preferredTrigger);
			}
		}
		const entries = new Map<
			KeybindCommand,
			{
				id: KeybindCommand;
				description: string;
				preferredTrigger?: string;
			}
		>();
		for (const keybind of Keybind.getDefaults()) {
			if (!keybind.allowGlobal) continue;
			if (!keybind.assignable && !keybind.combo.global) continue;
			entries.set(keybind.action, {
				id: keybind.action,
				description: keybind.label,
				preferredTrigger: activePreferredTriggers.get(keybind.action),
			});
		}
		return [...entries.values()];
	}
	private getOrderedGuilds(): Array<Guild> {
		if (GuildList.guilds.length > 0) {
			return GuildList.guilds;
		}
		return Guilds.getGuilds();
	}
	private navigateToDirectMessages(): void {
		const channelId = SelectedChannel.selectedChannelIds.get(ME);
		if (channelId && Channels.getChannel(channelId)) {
			this.navigateToChannel(ME, channelId);
			return;
		}
		NavigationCommands.deselectGuild();
	}
	private navigateToGuildLikeSlot(slotIndex: number): void {
		if (slotIndex === 0) {
			this.navigateToDirectMessages();
			return;
		}
		const guild = this.getOrderedGuilds()[slotIndex - 1];
		if (!guild) return;
		const channelId = SelectedChannel.selectedChannelIds.get(guild.id) ?? this.getFirstSelectableChannelId(guild.id);
		if (!channelId) return;
		this.navigateToChannel(guild.id, channelId);
	}
	private cycleGuildLikeSlot(direction: 1 | -1): void {
		const guilds = this.getOrderedGuilds();
		const currentGuildId = this.currentGuildId;
		const currentIndex = currentGuildId ? guilds.findIndex((guild) => guild.id === currentGuildId) + 1 : 0;
		const safeIndex = currentIndex <= 0 && currentGuildId ? 0 : currentIndex;
		const slotCount = guilds.length + 1;
		const nextIndex = (safeIndex + direction + slotCount) % slotCount;
		this.navigateToGuildLikeSlot(nextIndex);
	}
	private getFirstSelectableChannelId(guildId: string): string | undefined {
		const channels = Channels.getGuildChannels(guildId);
		const selectableChannel = channels.find((c) => this.isNavigableChannel(c));
		return selectableChannel?.id;
	}
	private isNavigableChannel(channel: Channel): boolean {
		if (channel.type === ChannelTypes.GUILD_CATEGORY) return false;
		if (channel.type === ChannelTypes.GUILD_LINK) return false;
		if (channel.guildId) {
			return Permission.can(Permissions.VIEW_CHANNEL, {
				channelId: channel.id,
				guildId: channel.guildId,
			});
		}
		return true;
	}
	private getNavigableChannelsInCurrentContext(): ReadonlyArray<Channel> {
		const guildId = this.currentGuildId;
		if (guildId) {
			return Channels.getGuildChannels(guildId).filter((c) => this.isNavigableChannel(c));
		}
		return [];
	}
	private cycleDirectMessageContext(direction: 1 | -1): void {
		const dmChannels = Channels.dmChannels;
		const slotCount = dmChannels.length + 1;
		const currentChannelId = this.currentChannelId;
		const currentIndex = currentChannelId ? dmChannels.findIndex((channel) => channel.id === currentChannelId) + 1 : 0;
		const safeIndex = currentIndex <= 0 && currentChannelId ? 0 : currentIndex;
		const nextIndex = (safeIndex + direction + slotCount) % slotCount;
		if (nextIndex === 0) {
			NavigationCommands.deselectGuild();
			return;
		}
		const channel = dmChannels[nextIndex - 1];
		if (channel) {
			this.navigateToChannel(ME, channel.id);
		}
	}
	private cycleChannelInCurrentContext(direction: 1 | -1): void {
		if (Navigation.context === 'dm') {
			this.cycleDirectMessageContext(direction);
			return;
		}
		const channels = this.getNavigableChannelsInCurrentContext();
		if (!channels.length) return;
		const current = this.currentChannelId;
		const idx = current ? channels.findIndex((c) => c.id === current) : -1;
		const base = idx === -1 ? (direction === 1 ? -1 : 0) : idx;
		const target = channels[(base + direction + channels.length) % channels.length];
		this.navigateToChannel(target.guildId ?? null, target.id);
	}
	private cycleFilteredChannelInCurrentGuild(predicate: (channel: Channel) => boolean, direction: 1 | -1): void {
		const guildId = this.currentGuildId;
		if (!guildId) return;
		const channels = Channels.getGuildChannels(guildId).filter((c) => this.isNavigableChannel(c) && predicate(c));
		if (!channels.length) return;
		const current = this.currentChannelId;
		const idx = current ? channels.findIndex((c) => c.id === current) : -1;
		const base = idx === -1 ? (direction === 1 ? -1 : 0) : idx;
		const target = channels[(base + direction + channels.length) % channels.length];
		this.navigateToChannel(guildId, target.id);
	}
	private isAppRoute(pathname: string): boolean {
		return pathname.startsWith('/channels/');
	}
	private ensureCombokeys(): CombokeysInstance | null {
		if (!this.combokeys && CombokeysImport) {
			this.combokeys = new CombokeysImport(document.documentElement);
			if (this.combokeys) {
				this.combokeys.stopCallback = () => false;
			}
		}
		return this.combokeys;
	}
	private async checkInputMonitoringPermission(): Promise<boolean> {
		if (!isNativeMacOS()) return true;
		if (this.accessibilityStatus === 'granted') return true;
		const result = await checkNativePermission('input-monitoring');
		if (result === 'granted') {
			this.accessibilityStatus = 'granted';
			return true;
		}
		if (result === 'denied') {
			this.accessibilityStatus = 'denied';
		} else {
			this.accessibilityStatus = 'unknown';
		}
		return false;
	}
	async init(i18n: I18n) {
		if (this.initialized) return;
		this.initialized = true;
		this.ensureCombokeys();
		this.registerDefaultHandlers(i18n);
		this.refreshLocalShortcuts();
		this.disposers.push(
			autorun(() => {
				this.refreshLocalShortcuts();
			}),
		);
		this.disposers.push(
			autorun(() => {
				void this.refreshGlobalShortcuts();
			}),
		);
		this.disposers.push(
			reaction(
				() => Keybind.transmitMode,
				() => {
					MediaEngine.handlePushToTalkModeChange();
				},
			),
		);
		this.disposers.push(
			autorun(() => {
				void this.refreshHoldInputs();
			}),
		);
		this.disposers.push(
			autorun(() => {
				const pathname = Navigation.pathname;
				const isAppRoute = this.isAppRoute(pathname);
				this.setRouteSuspended(!isAppRoute);
			}),
		);
		await this.refreshGlobalShortcuts();
		await this.refreshHoldInputs();
	}
	private buildHoldBindings(): Array<HoldBindingRuntime> {
		const bindings: Array<HoldBindingRuntime> = [];
		const inPttMode = Keybind.isPushToTalkEnabled();
		const eligibleActions = inPttMode ? HOLD_ACTIONS_FOR_PTT_MODE : HOLD_ACTIONS_FOR_VOICE_ACTIVITY_MODE;
		const customs: ReadonlyArray<CustomKeybindEntry> = Keybind.getCustomKeybinds();
		const pushBinding = (action: HoldAction, combo: KeyCombo): void => {
			const hasBinding = !!(combo.key || combo.code || combo.gamepadButton != null || combo.mouseButton != null);
			if (!hasBinding) return;
			bindings.push({
				action,
				combo,
				keycode: null,
				mouseButton: combo.mouseButton ?? null,
				gamepadButton: combo.gamepadButton ?? null,
				isModifierOnly: Boolean(combo.modifierOnly),
				ctrlOrMeta: Boolean(combo.ctrlOrMeta),
				requireBothSides: Boolean(combo.modifierOnly && combo.bothSides),
				modifiers: {
					ctrl: Boolean(combo.ctrl),
					alt: Boolean(combo.alt),
					shift: Boolean(combo.shift),
					meta: Boolean(combo.meta),
				},
				routing: null,
				pressedKeycodes: new Set<number>(),
				localPressedCodes: new Set<string>(),
				localActiveCode: null,
				localMouseActive: false,
				localKeyDown: null,
				localKeyUp: null,
				localMouseDown: null,
				localMouseUp: null,
				gamepadHeld: false,
			});
		};
		for (const action of eligibleActions) {
			for (const entry of customs) {
				if (!entry.enabled) continue;
				if (entry.action !== action) continue;
				pushBinding(action, entry.combo);
			}
		}
		return bindings;
	}
	private async refreshHoldInputs(): Promise<void> {
		this.detachLocalHoldListener();
		this.releaseGamepadHoldBindings();
		const bindings = this.buildHoldBindings();
		this.holdBindings = bindings;
		if (bindings.length === 0) {
			this.maybeStopGlobalKeyHook();
			this.refreshGamepadPolling();
			return;
		}
		const electronApi = getElectronAPI();
		const globalHookAvailable = !!electronApi?.globalKeyHookStart;
		const wantsGlobal = bindings.some((b) => {
			const hasGlobalRoutable = !!(b.combo.key || b.combo.code || b.mouseButton != null);
			return hasGlobalRoutable && (b.combo.global ?? false) && globalHookAvailable;
		});
		let globalReady = false;
		if (wantsGlobal) {
			globalReady = await this.startGlobalKeyHook();
			if (!globalReady) {
				this.maybeStopGlobalKeyHook();
			}
		} else {
			this.maybeStopGlobalKeyHook();
		}
		let needsLocal = false;
		for (const binding of bindings) {
			const hasGlobalRoutable = !!(binding.combo.key || binding.combo.code || binding.mouseButton != null);
			if (globalReady && hasGlobalRoutable && (binding.combo.global ?? false)) {
				binding.keycode = jsKeyToUiohookKeycode(binding.combo.code ?? binding.combo.key);
				binding.routing = 'global';
			} else if (hasGlobalRoutable || binding.isModifierOnly) {
				binding.routing = 'local';
				needsLocal = true;
			} else {
				binding.routing = null;
			}
		}
		if (needsLocal) {
			this.attachLocalHoldListener();
		}
		this.refreshGamepadPolling();
	}
	private attachLocalHoldListener(): void {
		if (this.localHoldListenerAttached) {
			this.detachLocalHoldListener();
		}
		this.localHoldListenerAttached = true;
		const onKeyDown = (event: KeyboardEvent): void => {
			for (const binding of this.holdBindings) {
				if (binding.routing !== 'local') continue;
				if (binding.isModifierOnly) {
					if (!this.localKeyEventMatchesModifierOnly(binding, event)) continue;
					if (binding.localPressedCodes.has(event.code)) continue;
					binding.localPressedCodes.add(event.code);
					const required = binding.requireBothSides ? 2 : 1;
					if (binding.localPressedCodes.size === required) {
						this.fireHoldHandler(binding, 'press', 'local');
					}
					continue;
				}
				if (!this.localKeyEventMatchesBinding(binding, event)) continue;
				if (binding.localActiveCode === event.code) continue;
				binding.localActiveCode = event.code;
				this.fireHoldHandler(binding, 'press', 'local');
			}
		};
		const onKeyUp = (event: KeyboardEvent): void => {
			for (const binding of this.holdBindings) {
				if (binding.routing !== 'local') continue;
				if (binding.isModifierOnly) {
					if (!binding.localPressedCodes.has(event.code)) continue;
					const required = binding.requireBothSides ? 2 : 1;
					const wasAtThreshold = binding.localPressedCodes.size === required;
					binding.localPressedCodes.delete(event.code);
					if (wasAtThreshold) {
						this.fireHoldHandler(binding, 'release', 'local');
					}
					continue;
				}
				if (binding.localActiveCode !== event.code) continue;
				binding.localActiveCode = null;
				this.fireHoldHandler(binding, 'release', 'local');
			}
		};
		const onMouseDown = (event: MouseEvent): void => {
			for (const binding of this.holdBindings) {
				if (binding.routing !== 'local') continue;
				if (binding.mouseButton === null) continue;
				if (event.button !== binding.mouseButton) continue;
				if (!this.matchesModifiers(binding, event)) continue;
				if (binding.localMouseActive) continue;
				binding.localMouseActive = true;
				this.fireHoldHandler(binding, 'press', 'local');
			}
		};
		const onMouseUp = (event: MouseEvent): void => {
			for (const binding of this.holdBindings) {
				if (binding.routing !== 'local') continue;
				if (binding.mouseButton === null) continue;
				if (event.button !== binding.mouseButton) continue;
				if (!binding.localMouseActive) continue;
				binding.localMouseActive = false;
				this.fireHoldHandler(binding, 'release', 'local');
			}
		};
		for (const binding of this.holdBindings) {
			binding.localKeyDown = onKeyDown;
			binding.localKeyUp = onKeyUp;
			binding.localMouseDown = onMouseDown;
			binding.localMouseUp = onMouseUp;
		}
		const onWindowBlur = (): void => {
			for (const binding of this.holdBindings) {
				if (binding.routing !== 'local') continue;
				if (binding.localActiveCode !== null) {
					binding.localActiveCode = null;
					this.fireHoldHandler(binding, 'release', 'local');
				}
				if (binding.localPressedCodes.size > 0) {
					const required = binding.requireBothSides ? 2 : 1;
					const wasAtThreshold = binding.localPressedCodes.size >= required;
					binding.localPressedCodes.clear();
					if (wasAtThreshold) {
						this.fireHoldHandler(binding, 'release', 'local');
					}
				}
				if (binding.localMouseActive) {
					binding.localMouseActive = false;
					this.fireHoldHandler(binding, 'release', 'local');
				}
			}
		};
		window.addEventListener('keydown', onKeyDown, true);
		window.addEventListener('keyup', onKeyUp, true);
		window.addEventListener('mousedown', onMouseDown, true);
		document.addEventListener('mouseup', onMouseUp, true);
		window.addEventListener('blur', onWindowBlur);
		this.localKeyDownRef = onKeyDown;
		this.localKeyUpRef = onKeyUp;
		this.localMouseDownRef = onMouseDown;
		this.localMouseUpRef = onMouseUp;
		this.localBlurRef = onWindowBlur;
	}
	private localKeyDownRef: ((event: KeyboardEvent) => void) | null = null;
	private localKeyUpRef: ((event: KeyboardEvent) => void) | null = null;
	private localMouseDownRef: ((event: MouseEvent) => void) | null = null;
	private localMouseUpRef: ((event: MouseEvent) => void) | null = null;
	private localBlurRef: (() => void) | null = null;
	private localShortcutMouseDownRef: ((event: MouseEvent) => void) | null = null;
	private localShortcutMouseUpRef: ((event: MouseEvent) => void) | null = null;
	private detachLocalHoldListener(): void {
		if (!this.localHoldListenerAttached) return;
		this.localHoldListenerAttached = false;
		if (this.localKeyDownRef) window.removeEventListener('keydown', this.localKeyDownRef, true);
		if (this.localKeyUpRef) window.removeEventListener('keyup', this.localKeyUpRef, true);
		if (this.localMouseDownRef) window.removeEventListener('mousedown', this.localMouseDownRef, true);
		if (this.localMouseUpRef) document.removeEventListener('mouseup', this.localMouseUpRef, true);
		if (this.localBlurRef) window.removeEventListener('blur', this.localBlurRef);
		this.localKeyDownRef = null;
		this.localKeyUpRef = null;
		this.localMouseDownRef = null;
		this.localMouseUpRef = null;
		this.localBlurRef = null;
		for (const binding of this.holdBindings) {
			if (binding.localActiveCode !== null) {
				binding.localActiveCode = null;
				this.fireHoldHandler(binding, 'release', 'local');
			}
			if (binding.localPressedCodes.size > 0) {
				const required = binding.requireBothSides ? 2 : 1;
				const wasAtThreshold = binding.localPressedCodes.size >= required;
				binding.localPressedCodes.clear();
				if (wasAtThreshold) {
					this.fireHoldHandler(binding, 'release', 'local');
				}
			}
			if (binding.localMouseActive) {
				binding.localMouseActive = false;
				this.fireHoldHandler(binding, 'release', 'local');
			}
			binding.localKeyDown = null;
			binding.localKeyUp = null;
			binding.localMouseDown = null;
			binding.localMouseUp = null;
		}
	}
	private refreshLocalMouseShortcutListener(): void {
		this.detachLocalMouseShortcutListener();
		if (this.activeMouseShortcutKeybinds.length === 0) return;
		this.localMouseShortcutListenerAttached = true;
		const onMouseDown = (event: MouseEvent): void => {
			this.handleLocalMouseShortcutEvent(event, 'press');
		};
		const onMouseUp = (event: MouseEvent): void => {
			this.handleLocalMouseShortcutEvent(event, 'release');
		};
		window.addEventListener('mousedown', onMouseDown, true);
		document.addEventListener('mouseup', onMouseUp, true);
		this.localShortcutMouseDownRef = onMouseDown;
		this.localShortcutMouseUpRef = onMouseUp;
	}
	private detachLocalMouseShortcutListener(): void {
		if (!this.localMouseShortcutListenerAttached) return;
		this.localMouseShortcutListenerAttached = false;
		if (this.localShortcutMouseDownRef) window.removeEventListener('mousedown', this.localShortcutMouseDownRef, true);
		if (this.localShortcutMouseUpRef) document.removeEventListener('mouseup', this.localShortcutMouseUpRef, true);
		this.localShortcutMouseDownRef = null;
		this.localShortcutMouseUpRef = null;
	}
	private handleLocalMouseShortcutEvent(event: MouseEvent, type: 'press' | 'release'): void {
		for (const binding of this.activeMouseShortcutKeybinds) {
			const combo = binding.combo;
			if (combo.mouseButton == null) continue;
			if (event.button !== combo.mouseButton) continue;
			if (!this.comboModifiersMatch(combo, event)) continue;
			if (this.isHookShortcutRegistered(binding)) continue;
			this.fireShortcutHandler(binding, type, 'local', {shiftKey: event.shiftKey});
		}
	}
	private localKeyEventMatchesBinding(binding: HoldBindingRuntime, event: KeyboardEvent): boolean {
		const expectedCode = binding.combo.code ?? null;
		const expectedKey = binding.combo.key ?? null;
		if (expectedCode && event.code !== expectedCode) return false;
		if (!expectedCode && expectedKey && event.key !== expectedKey) return false;
		if (!expectedCode && !expectedKey) return false;
		if (binding.isModifierOnly) return true;
		return this.matchesModifiers(binding, event);
	}
	private localKeyEventMatchesModifierOnly(binding: HoldBindingRuntime, event: KeyboardEvent): boolean {
		const {modifiers, ctrlOrMeta} = binding;
		const eventKey = event.key;
		if (ctrlOrMeta) {
			if (isNativeMacOS()) return eventKey === 'Meta';
			return eventKey === 'Control';
		}
		if (modifiers.ctrl && eventKey === 'Control') return true;
		if (modifiers.alt && eventKey === 'Alt') return true;
		if (modifiers.shift && eventKey === 'Shift') return true;
		if (modifiers.meta && eventKey === 'Meta') return true;
		return false;
	}
	private matchesModifiers(binding: HoldBindingRuntime, event: KeyboardEvent | MouseEvent): boolean {
		return this.comboModifiersMatch(binding.combo, event);
	}
	private comboModifiersMatch(combo: KeyCombo, event: KeyboardEvent | MouseEvent): boolean {
		const anyModifier = Boolean(combo.ctrlOrMeta || combo.ctrl || combo.meta || combo.alt || combo.shift);
		if (!anyModifier) return true;
		if (combo.ctrlOrMeta && (isNativeMacOS() ? !event.metaKey : !event.ctrlKey)) return false;
		if (combo.ctrl !== undefined && event.ctrlKey !== Boolean(combo.ctrl)) return false;
		if (combo.meta !== undefined && event.metaKey !== Boolean(combo.meta)) return false;
		if (combo.alt !== undefined && event.altKey !== Boolean(combo.alt)) return false;
		if (combo.shift !== undefined && event.shiftKey !== Boolean(combo.shift)) return false;
		return true;
	}
	private hasGamepadBindings(): boolean {
		return (
			this.holdBindings.some((binding) => binding.gamepadButton !== null) ||
			this.activeGamepadShortcutKeybinds.length > 0
		);
	}
	private refreshGamepadPolling(): void {
		if (this.hasGamepadBindings()) {
			this.startGamepadPolling();
		} else {
			this.stopGamepadPolling();
		}
	}
	private startGamepadPolling(): void {
		if (this.gamepadRafId !== null) return;
		if (typeof navigator.getGamepads !== 'function') return;
		if (!this.gamepadListenersAttached) {
			this.gamepadListenersAttached = true;
			window.addEventListener('gamepadconnected', this.handleGamepadConnected);
			window.addEventListener('gamepaddisconnected', this.handleGamepadDisconnected);
		}
		const tick = () => {
			this.tickGamepadBindings();
			this.gamepadRafId = requestAnimationFrame(tick);
		};
		this.gamepadRafId = requestAnimationFrame(tick);
	}
	private stopGamepadPolling(): void {
		if (this.gamepadRafId !== null) {
			cancelAnimationFrame(this.gamepadRafId);
			this.gamepadRafId = null;
		}
		if (this.gamepadListenersAttached) {
			this.gamepadListenersAttached = false;
			window.removeEventListener('gamepadconnected', this.handleGamepadConnected);
			window.removeEventListener('gamepaddisconnected', this.handleGamepadDisconnected);
		}
		this.releaseGamepadShortcutStates();
		this.releaseGamepadHoldBindings();
	}
	private releaseGamepadHoldBindings(): void {
		for (const binding of this.holdBindings) {
			if (binding.gamepadHeld) {
				binding.gamepadHeld = false;
				this.fireHoldHandler(binding, 'release', 'local');
			}
		}
	}
	private releaseGamepadShortcutStates(): void {
		for (const {binding, pressed} of this.gamepadShortcutStates.values()) {
			if (pressed) {
				this.fireShortcutHandler(binding, 'release', 'local');
			}
		}
		this.gamepadShortcutStates.clear();
	}
	private handleGamepadConnected = (): void => {
		for (const binding of this.holdBindings) binding.gamepadHeld = false;
		this.gamepadShortcutStates.clear();
	};
	private handleGamepadDisconnected = (): void => {
		this.releaseGamepadHoldBindings();
		this.releaseGamepadShortcutStates();
	};
	private tickGamepadBindings(): void {
		const pads = navigator.getGamepads?.() ?? [];
		for (const binding of this.holdBindings) {
			const target = binding.gamepadButton;
			if (target === null) continue;
			const pressed = this.isGamepadButtonPressed(pads, target);
			if (pressed === binding.gamepadHeld) continue;
			binding.gamepadHeld = pressed;
			this.fireHoldHandler(binding, pressed ? 'press' : 'release', 'local');
		}
		for (const binding of this.activeGamepadShortcutKeybinds) {
			const target = binding.combo.gamepadButton;
			if (target == null) continue;
			const id = hookShortcutIdForKeybind(binding) ?? `gamepad:${binding.action}:${target}`;
			const pressed = this.isGamepadButtonPressed(pads, target);
			const previous = this.gamepadShortcutStates.get(id)?.pressed ?? false;
			if (pressed === previous) continue;
			this.gamepadShortcutStates.set(id, {binding, pressed});
			this.fireShortcutHandler(binding, pressed ? 'press' : 'release', 'local');
		}
	}
	private isGamepadButtonPressed(pads: ReadonlyArray<Gamepad | null>, target: number): boolean {
		for (const pad of pads) {
			if (!pad) continue;
			const btn = pad.buttons[target];
			if (btn?.pressed) return true;
		}
		return false;
	}
	private fireHoldHandler(binding: HoldBindingRuntime, type: 'press' | 'release', source: ShortcutSource): void {
		if (this.suspended) return;
		if (Keybind.isActionMuted(binding.action)) return;
		const handler = this.handlers.get(binding.action);
		if (!handler) return;
		handler({type, source});
	}
	private fireShortcutHandler(
		binding: RuntimeKeybind,
		type: 'press' | 'release',
		source: ShortcutSource,
		options: {shiftKey?: boolean} = {},
	): void {
		if (this.suspended) return;
		if (Keybind.isActionMuted(binding.action)) return;
		const handler = this.handlers.get(binding.action);
		if (!handler) return;
		handler({type, source, shiftKey: options.shiftKey});
	}
	async reapplyGlobalShortcuts() {
		if (!this.initialized) return;
		await this.refreshGlobalShortcuts();
	}
	destroy() {
		if (!this.initialized) return;
		this.initialized = false;
		this.disposers.forEach((dispose) => dispose());
		this.disposers = [];
		if (this.globalShortcutUnsubscribe) {
			this.globalShortcutUnsubscribe();
			this.globalShortcutUnsubscribe = null;
		}
		if (this.globalKeybindTriggeredUnsubscribe) {
			this.globalKeybindTriggeredUnsubscribe();
			this.globalKeybindTriggeredUnsubscribe = null;
		}
		if (this.globalShortcutPortalBindingsUnsubscribe) {
			this.globalShortcutPortalBindingsUnsubscribe();
			this.globalShortcutPortalBindingsUnsubscribe = null;
		}
		Keybind.setPortalShortcutBindings([]);
		this.stopGlobalKeyHook();
		this.detachLocalHoldListener();
		this.detachLocalMouseShortcutListener();
		this.stopGamepadPolling();
		this.holdBindings = [];
		const electronApi = getElectronAPI();
		if (electronApi) {
			void electronApi.unregisterAllGlobalShortcuts?.().catch(() => {});
			if (this.hookFallbackShortcutIds.size > 0) {
				void electronApi.globalKeyHookUnregisterAll?.().catch(() => {});
			}
		}
		this.registeredGlobalShortcutIds.clear();
		this.hookFallbackShortcutIds.clear();
		this.registeredPortalShortcutActionIds.clear();
		this.handlers.clear();
		this.combokeys?.detach();
		this.combokeys = null;
	}
	async startGlobalKeyHook(): Promise<boolean> {
		const electronApi = getElectronAPI();
		if (!electronApi?.globalKeyHookStart) return false;
		if (this.globalKeyHookStarted) return true;
		if (!(await this.checkInputMonitoringPermission())) {
			return false;
		}
		const started = await electronApi.globalKeyHookStart();
		if (!started) return false;
		this.globalKeyHookStarted = true;
		const keyEventUnsub = electronApi.onGlobalKeyEvent?.((event) => {
			this.handleGlobalKeyEvent(
				event as {
					type: 'keydown' | 'keyup';
					keycode: number;
					keyName: string;
					ctrlKey: boolean;
					altKey: boolean;
					shiftKey: boolean;
					metaKey: boolean;
				},
			);
		});
		if (keyEventUnsub) this.globalKeyHookUnsubscribes.push(keyEventUnsub);
		const mouseEventUnsub = electronApi.onGlobalMouseEvent?.((event) => {
			this.handleGlobalMouseEvent(
				event as {
					type: 'mousedown' | 'mouseup';
					button: number;
					ctrlKey: boolean;
					altKey: boolean;
					shiftKey: boolean;
					metaKey: boolean;
				},
			);
		});
		if (mouseEventUnsub) this.globalKeyHookUnsubscribes.push(mouseEventUnsub);
		this.ensureGlobalKeybindTriggeredSubscription();
		return true;
	}
	private ensureGlobalShortcutPortalBindingsSubscription(): void {
		if (this.globalShortcutPortalBindingsUnsubscribe) return;
		const electronApi = getElectronAPI();
		this.globalShortcutPortalBindingsUnsubscribe =
			electronApi?.onGlobalShortcutPortalBindings?.((shortcuts) => {
				Keybind.setPortalShortcutBindings(shortcuts);
			}) ?? null;
	}
	private ensureGlobalKeybindTriggeredSubscription(): void {
		if (this.globalKeybindTriggeredUnsubscribe) return;
		const electronApi = getElectronAPI();
		this.globalKeybindTriggeredUnsubscribe =
			electronApi?.onGlobalKeybindTriggered?.((event) => {
				if (
					!this.hookFallbackShortcutIds.has(event.id) &&
					!this.registeredPortalShortcutActionIds.has(event.id as KeybindCommand)
				) {
					return;
				}
				const keybind = this.resolveGlobalShortcutEventId(event.id);
				if (!keybind) return;
				if (!HOLD_ACTIONS.includes(keybind.action as HoldAction) && event.type !== 'keydown') return;
				const handler = this.handlers.get(keybind.action);
				if (!handler) return;
				if (this.suspended) return;
				if (Keybind.isActionMuted(keybind.action)) return;
				handler({type: event.type === 'keydown' ? 'press' : 'release', source: 'global'});
			}) ?? null;
	}
	stopGlobalKeyHook(): void {
		const electronApi = getElectronAPI();
		this.globalKeyHookUnsubscribes.forEach((unsub) => unsub());
		this.globalKeyHookUnsubscribes = [];
		if (electronApi?.globalKeyHookStop && this.globalKeyHookStarted) {
			void electronApi.globalKeyHookStop();
		}
		this.globalKeyHookStarted = false;
	}
	private maybeStopGlobalKeyHook(): void {
		if (this.hookFallbackShortcutIds.size > 0) return;
		this.stopGlobalKeyHook();
	}
	private handleGlobalKeyEvent(event: {
		type: 'keydown' | 'keyup';
		keycode: number;
		keyName: string;
		ctrlKey: boolean;
		altKey: boolean;
		shiftKey: boolean;
		metaKey: boolean;
	}): void {
		for (const binding of this.holdBindings) {
			if (binding.routing !== 'global') continue;
			this.handleGlobalKeyEventForBinding(binding, event);
		}
	}
	private handleGlobalKeyEventForBinding(
		binding: HoldBindingRuntime,
		event: {
			type: 'keydown' | 'keyup';
			keycode: number;
			keyName: string;
			ctrlKey: boolean;
			altKey: boolean;
			shiftKey: boolean;
			metaKey: boolean;
		},
	): void {
		if (binding.isModifierOnly) {
			if (!this.isHoldModifierEvent(binding, event)) return;
			const requiredCount = binding.requireBothSides ? 2 : 1;
			if (event.type === 'keydown') {
				if (binding.pressedKeycodes.has(event.keycode)) return;
				binding.pressedKeycodes.add(event.keycode);
				if (binding.pressedKeycodes.size === requiredCount) {
					this.fireHoldHandler(binding, 'press', 'global');
				}
			} else {
				if (!binding.pressedKeycodes.has(event.keycode)) return;
				const wasAtThreshold = binding.pressedKeycodes.size === requiredCount;
				binding.pressedKeycodes.delete(event.keycode);
				if (wasAtThreshold) {
					this.fireHoldHandler(binding, 'release', 'global');
				}
			}
			return;
		}
		if (binding.keycode === null) return;
		if (event.keycode !== binding.keycode) return;
		if (!this.globalHoldModifiersMatch(binding, event)) return;
		this.fireHoldHandler(binding, event.type === 'keydown' ? 'press' : 'release', 'global');
	}
	private isHoldPrimaryModifierDown(event: {ctrlKey: boolean; metaKey: boolean}): boolean {
		return isNativeMacOS() ? event.metaKey : event.ctrlKey;
	}
	private globalHoldModifiersMatch(
		binding: HoldBindingRuntime,
		event: {
			ctrlKey: boolean;
			altKey: boolean;
			shiftKey: boolean;
			metaKey: boolean;
		},
	): boolean {
		const {modifiers, ctrlOrMeta} = binding;
		const anyModifierExpected = ctrlOrMeta || modifiers.ctrl || modifiers.alt || modifiers.shift || modifiers.meta;
		if (!anyModifierExpected) return true;
		if (ctrlOrMeta && !this.isHoldPrimaryModifierDown(event)) return false;
		if (event.ctrlKey !== modifiers.ctrl && !ctrlOrMeta) return false;
		if (event.metaKey !== modifiers.meta && !ctrlOrMeta) return false;
		if (modifiers.ctrl && !event.ctrlKey) return false;
		if (modifiers.meta && !event.metaKey) return false;
		if (event.altKey !== modifiers.alt) return false;
		if (event.shiftKey !== modifiers.shift) return false;
		return true;
	}
	private isHoldModifierEvent(
		binding: HoldBindingRuntime,
		event: {
			keycode: number;
			keyName: string;
		},
	): boolean {
		const {modifiers, ctrlOrMeta} = binding;
		const keyName = event.keyName;
		if (ctrlOrMeta) {
			if (isNativeMacOS()) {
				return keyName === 'MetaLeft' || keyName === 'MetaRight';
			}
			return keyName === 'ControlLeft' || keyName === 'ControlRight';
		}
		if (modifiers.ctrl && (keyName === 'ControlLeft' || keyName === 'ControlRight')) return true;
		if (modifiers.alt && (keyName === 'AltLeft' || keyName === 'AltRight')) return true;
		if (modifiers.shift && (keyName === 'ShiftLeft' || keyName === 'ShiftRight')) return true;
		if (modifiers.meta && (keyName === 'MetaLeft' || keyName === 'MetaRight')) return true;
		return false;
	}
	private handleGlobalMouseEvent(event: {
		type: 'mousedown' | 'mouseup';
		button: number;
		ctrlKey: boolean;
		altKey: boolean;
		shiftKey: boolean;
		metaKey: boolean;
	}): void {
		for (const binding of this.holdBindings) {
			if (binding.routing !== 'global') continue;
			if (binding.mouseButton === null) continue;
			if (event.button !== binding.mouseButton) continue;
			if (!this.globalHoldModifiersMatch(binding, event)) continue;
			this.fireHoldHandler(binding, event.type === 'mousedown' ? 'press' : 'release', 'global');
		}
	}
	suspend(): void {
		this.manualSuspendCount += 1;
		this.combokeys?.reset();
	}
	resume(): void {
		this.manualSuspendCount = Math.max(0, this.manualSuspendCount - 1);
		if (!this.suspended) {
			this.refreshLocalShortcuts();
		}
	}
	isSuspended(): boolean {
		return this.suspended;
	}
	private setRouteSuspended(value: boolean): void {
		if (this.routeSuspended === value) return;
		this.routeSuspended = value;
		if (value) {
			this.combokeys?.reset();
		} else if (!this.suspended) {
			this.refreshLocalShortcuts();
		}
	}
	register(action: KeybindCommand, handler: KeybindHandler) {
		this.handlers.set(action, handler);
	}
	private registerDefaultHandlers(i18n: I18n) {
		this.register('nav_quick_switcher', ({type}) => {
			if (type !== 'press') return;
			if (QuickSwitcher.getIsOpen()) QuickSwitcher.hide();
			else QuickSwitcher.show();
		});
		this.register('system_toggle_shortcuts_overlay', ({type}) => {
			if (type !== 'press') return;
			ModalCommands.push(modal(() => React.createElement(UserSettingsModal, {initialTab: 'keybinds'})));
			ComponentDispatch.dispatch('USER_SETTINGS_TAB_SELECT', {tab: 'keybinds'});
		});
		this.register('misc_help', ({type}) => {
			if (type !== 'press') return;
			openExternalUrlWithWarning(Routes.help());
		});
		this.register('misc_search', ({type}) => {
			if (type !== 'press') return;
			ComponentDispatch.dispatch('MESSAGE_SEARCH_OPEN');
		});
		this.register('voice_toggle_mute', ({type}) => {
			if (type !== 'press') return;
			const connectedGuildId = MediaEngine.guildId;
			const voiceState = MediaEngine.getVoiceState(connectedGuildId);
			const isGuildMuted = voiceState?.mute ?? false;
			if (isGuildMuted) {
				ModalCommands.push(
					modal(() =>
						React.createElement(ConfirmModal, {
							title: i18n._(msg`Muted by moderators`),
							description: i18n._(msg`You can't unmute yourself because a moderator muted you.`),
							primaryText: i18n._(msg`Okay`),
							primaryVariant: 'primary',
							secondaryText: false,
							onPrimary: () => {},
							hideCloseButton: true,
						}),
					),
				);
				return;
			}
			VoiceStateCommands.toggleSelfMute(null).catch((error) =>
				this.logger.error('voice_toggle_mute keybind failed', error),
			);
		});
		this.register('voice_toggle_deafen', ({type}) => {
			if (type !== 'press') return;
			const connectedGuildId = MediaEngine.guildId;
			const voiceState = MediaEngine.getVoiceState(connectedGuildId);
			const isGuildDeafened = voiceState?.deaf ?? false;
			if (isGuildDeafened) {
				ModalCommands.push(
					modal(() =>
						React.createElement(ConfirmModal, {
							title: i18n._(msg`Deafened by moderators`),
							description: i18n._(msg`You can't undeafen yourself because a moderator deafened you.`),
							primaryText: i18n._(msg`Okay`),
							primaryVariant: 'primary',
							secondaryText: false,
							onPrimary: () => {},
							hideCloseButton: true,
						}),
					),
				);
				return;
			}
			VoiceStateCommands.toggleSelfDeaf(null).catch((error) =>
				this.logger.error('voice_toggle_deafen keybind failed', error),
			);
		});
		this.register('system_toggle_settings', ({type}) => {
			if (type !== 'press') return;
			ModalCommands.push(modal(() => React.createElement(UserSettingsModal)));
		});
		this.register('voice_push_to_talk', ({type}) => {
			if (!Keybind.isPushToTalkEnabled()) return;
			if (type === 'press') {
				if (this.pttReleaseTimer) {
					clearTimeout(this.pttReleaseTimer);
					this.pttReleaseTimer = null;
				}
				const shouldUnmute = Keybind.handlePushToTalkPress();
				if (shouldUnmute) {
					MediaEngine.applyPushToTalkHold(true);
				}
			} else {
				const shouldMute = Keybind.handlePushToTalkRelease();
				if (shouldMute) {
					const delay = Keybind.pushToTalkReleaseDelay;
					this.pttReleaseTimer = setTimeout(() => {
						this.pttReleaseTimer = null;
						MediaEngine.applyPushToTalkHold(false);
					}, delay);
				}
			}
		});
		this.register('voice_push_to_mute', ({type}) => {
			if (Keybind.isPushToTalkEnabled()) return;
			MediaEngine.applyPushToMuteHold(type === 'press');
		});
		this.register('voice_push_to_talk_priority', ({type}) => {
			if (!Keybind.isPushToTalkEnabled()) return;
			if (type === 'press') {
				if (this.pttReleaseTimer) {
					clearTimeout(this.pttReleaseTimer);
					this.pttReleaseTimer = null;
				}
				Keybind.handlePushToTalkPress();
				Keybind.setPrioritySpeakerHeld(true);
				MediaEngine.applyPushToTalkHold(true);
			} else {
				Keybind.handlePushToTalkRelease();
				Keybind.setPrioritySpeakerHeld(false);
				const delay = Keybind.pushToTalkReleaseDelay;
				this.pttReleaseTimer = setTimeout(() => {
					this.pttReleaseTimer = null;
					MediaEngine.applyPushToTalkHold(false);
				}, delay);
			}
		});
		this.register('voice_priority_vad', ({type}) => {
			if (Keybind.isPushToTalkEnabled()) return;
			Keybind.setPrioritySpeakerHeld(type === 'press');
		});
		this.register('voice_toggle_vad', ({type}) => {
			if (type !== 'press') return;
			Keybind.toggleTransmitMode();
		});
		this.register('voice_toggle_camera', ({type}) => {
			if (type !== 'press') return;
			void MediaEngine.toggleCameraFromKeybind().catch((error) =>
				this.logger.error('voice_toggle_camera keybind failed', error),
			);
		});
		this.register('voice_switch_channel', ({type}) => {
			if (type !== 'press') return;
			const channelId = this.currentChannelId;
			if (!channelId) return;
			const channel = Channels.getChannel(channelId);
			if (!channel) return;
			if (channel.type !== ChannelTypes.GUILD_VOICE) {
				return;
			}
			void MediaEngine.connectToVoiceChannel(channel.guildId ?? null, channel.id).catch((error) =>
				this.logger.error('voice_switch_channel keybind failed', error),
			);
		});
		this.register('voice_disconnect', ({type}) => {
			if (type !== 'press') return;
			void MediaEngine.disconnectFromVoiceChannel('user').catch((error) =>
				this.logger.error('voice_disconnect keybind failed', error),
			);
		});
		this.register('voice_toggle_soundboard', ({type}) => {
			if (type !== 'press') return;
			ComponentDispatch.dispatch('SOUNDBOARD_TOGGLE');
		});
		this.register('chat_copy_channel_link', ({type}) => {
			if (type !== 'press') return;
			const channelId = this.currentChannelId;
			if (!channelId) return;
			const channel = Channels.getChannel(channelId);
			if (!channel) return;
			const link = channel.guildId
				? `${window.location.origin}/channels/${channel.guildId}/${channel.id}`
				: `${window.location.origin}/channels/@me/${channel.id}`;
			void navigator.clipboard?.writeText(link).catch(() => {});
		});
		this.register('message_focus_textarea', ({type}) => {
			if (type !== 'press') return;
			const channelId = this.currentChannelId;
			if (!channelId) return;
			ComponentDispatch.dispatch('FOCUS_TEXTAREA', {channelId});
		});
		this.register('chat_scroll_up', ({type}) => {
			if (type !== 'press') return;
			ComponentDispatch.dispatch('SCROLL_PAGE_UP');
		});
		this.register('chat_scroll_down', ({type}) => {
			if (type !== 'press') return;
			ComponentDispatch.dispatch('SCROLL_PAGE_DOWN');
		});
		this.register('chat_jump_oldest_unread', ({type}) => {
			if (type !== 'press') return;
			const channelId = this.currentChannelId;
			if (!channelId) return;
			const targetId = ReadStates.getOldestUnreadMessageId(channelId);
			if (!targetId) return;
			goToMessage(channelId, targetId, {jumpType: JumpTypes.ANIMATED});
		});
		this.register('chat_mark_channel_read', ({type}) => {
			if (type !== 'press') return;
			const channelId = this.currentChannelId;
			if (!channelId) return;
			if (ReadStates.hasUnread(channelId)) {
				ReadStateCommands.ack(channelId, true, true);
			}
		});
		this.register('chat_mark_guild_read', ({type}) => {
			if (type !== 'press') return;
			const guildId = this.currentGuildId;
			if (!guildId) return;
			const channels = Channels.getGuildChannels(guildId);
			const channelIds = channels.filter((channel) => ReadStates.hasUnread(channel.id)).map((channel) => channel.id);
			if (channelIds.length > 0) {
				void ReadStateCommands.bulkAckChannels(channelIds);
			}
		});
		this.register('chat_mark_all_inbox_read', ({type}) => {
			if (type !== 'press') return;
			InboxCommands.markAllInboxChannelsAsRead(i18n);
		});
		this.register('chat_mark_inbox_read', ({type}) => {
			if (type !== 'press') return;
			const inboxTab = Inbox.selectedTab;
			if (inboxTab === 'unreadChannels') {
				const unreadChannelId = ReadStates.getChannelIds().find((channelId) =>
					ReadStates.hasUnreadOrMentions(channelId),
				);
				if (unreadChannelId) {
					ReadStateCommands.ack(unreadChannelId, true, true);
				}
			} else if (inboxTab === 'bookmarks') {
				const savedMessages = SavedMessages.savedMessages;
				const unreadBookmark = savedMessages.find((message) => {
					return ReadStates.hasUnread(message.channelId);
				});
				if (unreadBookmark) {
					ReadStateCommands.ack(unreadBookmark.channelId, true, true);
				}
			} else {
				const mentions = MentionFeed.recentMentions;
				const unreadMention = mentions.find((message) => {
					return ReadStates.hasUnread(message.channelId);
				});
				if (unreadMention) {
					ReadStateCommands.ack(unreadMention.channelId, true, true);
				}
			}
		});
		this.register('message_edit', ({type, context}) => {
			if (type !== 'press') return;
			const message = context?.focusedMessage ?? MessageFocus.getFocusedMessage();
			if (!message) return;
			startMessageEdit(message);
		});
		this.register('message_delete', ({type, context}) => {
			if (type !== 'press') return;
			const message = context?.focusedMessage ?? MessageFocus.getFocusedMessage();
			if (!message) return;
			requestDeleteMessage(message, i18n);
		});
		this.register('message_pin', ({type, context}) => {
			if (type !== 'press') return;
			const message = context?.focusedMessage ?? MessageFocus.getFocusedMessage();
			if (!message) return;
			requestMessagePin(message, i18n);
		});
		this.register('message_react', ({type, context}) => {
			if (type !== 'press') return;
			const message = context?.focusedMessage ?? MessageFocus.getFocusedMessage();
			if (!message) return;
			triggerAddReaction(message);
		});
		this.register('message_reply', ({type, context}) => {
			if (type !== 'press') return;
			const message = context?.focusedMessage ?? MessageFocus.getFocusedMessage();
			if (!message) return;
			requestMessageReply(message);
		});
		this.register('message_forward', ({type, context}) => {
			if (type !== 'press') return;
			const message = context?.focusedMessage ?? MessageFocus.getFocusedMessage();
			if (!message) return;
			requestMessageForward(message);
		});
		this.register('message_copy_text', ({type, context}) => {
			if (type !== 'press') return;
			const message = context?.focusedMessage ?? MessageFocus.getFocusedMessage();
			if (!message) return;
			requestCopyMessageText(message, i18n);
		});
		this.register('message_speak', ({type, context}) => {
			if (type !== 'press') return;
			const message = context?.focusedMessage ?? MessageFocus.getFocusedMessage();
			if (!message) return;
			requestSpeakMessage(message);
		});
		this.register('message_mark_unread', ({type, context}) => {
			if (type !== 'press') return;
			const message = context?.focusedMessage ?? MessageFocus.getFocusedMessage();
			if (!message) return;
			requestMarkMessageUnread(message);
		});
		this.register('message_bookmark', ({type, context}) => {
			if (type !== 'press') return;
			const message = context?.focusedMessage ?? MessageFocus.getFocusedMessage();
			if (!message) return;
			requestToggleBookmark(message, i18n);
		});
		this.register('message_toggle_embeds', ({type, context}) => {
			if (type !== 'press') return;
			const message = context?.focusedMessage ?? MessageFocus.getFocusedMessage();
			if (!message) return;
			requestToggleSuppressEmbeds(message, i18n);
		});
		this.register('message_copy_link', ({type, context}) => {
			if (type !== 'press') return;
			const message = context?.focusedMessage ?? MessageFocus.getFocusedMessage();
			if (!message) return;
			requestCopyMessageLink(message, i18n);
		});
		this.register('message_copy_id', ({type, context}) => {
			if (type !== 'press') return;
			const message = context?.focusedMessage ?? MessageFocus.getFocusedMessage();
			if (!message) return;
			requestCopyMessageId(message, i18n);
		});
		this.register('nav_history_back', ({type}) => {
			if (type !== 'press') return;
			if (SelectedChannel.navigateViewedChannelHistory(-1)) return;
			const history = RouterUtils.getHistory();
			if (history?.go) history.go(-1);
		});
		this.register('nav_history_forward', ({type}) => {
			if (type !== 'press') return;
			if (SelectedChannel.navigateViewedChannelHistory(1)) return;
			const history = RouterUtils.getHistory();
			if (history?.go) history.go(1);
		});
		this.register('nav_current_call', ({type}) => {
			if (type !== 'press') return;
			const channelId = MediaEngine.channelId;
			const guildId = MediaEngine.guildId;
			if (!channelId) return;
			this.navigateToChannel(guildId, channelId);
		});
		this.register('nav_toggle_last_guild_dms', ({type}) => {
			if (type !== 'press') return;
			const lastGuild = SelectedGuild.lastSelectedGuildId;
			const dmChannel = SelectedChannel.selectedChannelIds.get(ME);
			if (lastGuild) {
				const channelId = SelectedChannel.selectedChannelIds.get(lastGuild);
				if (channelId) {
					this.navigateToChannel(lastGuild, channelId);
					return;
				}
			}
			if (dmChannel) {
				this.navigateToChannel(ME, dmChannel);
			}
		});
		this.register('nav_guild_tab_next', ({type}) => {
			if (type !== 'press') return;
			this.cycleGuildLikeSlot(1);
		});
		this.register('nav_guild_tab_prev', ({type}) => {
			if (type !== 'press') return;
			this.cycleGuildLikeSlot(-1);
		});
		const guildSlotActions: Array<[KeybindCommand, number]> = [
			['nav_guild_slot_1', 0],
			['nav_guild_slot_2', 1],
			['nav_guild_slot_3', 2],
			['nav_guild_slot_4', 3],
			['nav_guild_slot_5', 4],
			['nav_guild_slot_6', 5],
			['nav_guild_slot_7', 6],
			['nav_guild_slot_8', 7],
			['nav_guild_slot_9', 8],
		];
		for (const [action, slotIndex] of guildSlotActions) {
			this.register(action, ({type}) => {
				if (type !== 'press') return;
				this.navigateToGuildLikeSlot(slotIndex);
			});
		}
		this.register('nav_channel_next', ({type}) => {
			if (type !== 'press') return;
			this.cycleChannelInCurrentContext(1);
		});
		this.register('nav_channel_prev', ({type}) => {
			if (type !== 'press') return;
			this.cycleChannelInCurrentContext(-1);
		});
		this.register('nav_guild_next', ({type}) => {
			if (type !== 'press') return;
			this.cycleGuildLikeSlot(1);
		});
		this.register('nav_guild_prev', ({type}) => {
			if (type !== 'press') return;
			this.cycleGuildLikeSlot(-1);
		});
		this.register('nav_unread_next', ({type}) => {
			if (type !== 'press') return;
			this.cycleFilteredChannelInCurrentGuild((c) => ReadStates.hasUnread(c.id), 1);
		});
		this.register('nav_unread_prev', ({type}) => {
			if (type !== 'press') return;
			this.cycleFilteredChannelInCurrentGuild((c) => ReadStates.hasUnread(c.id), -1);
		});
		this.register('nav_mention_next', ({type}) => {
			if (type !== 'press') return;
			this.cycleFilteredChannelInCurrentGuild((c) => ReadStates.getMentionCount(c.id) > 0, 1);
		});
		this.register('nav_mention_prev', ({type}) => {
			if (type !== 'press') return;
			this.cycleFilteredChannelInCurrentGuild((c) => ReadStates.getMentionCount(c.id) > 0, -1);
		});
		this.register('voice_start_dm_call', ({type, shiftKey}) => {
			if (type !== 'press') return;
			const channelId = this.currentChannelId;
			if (!channelId) return;
			const channel = Channels.getChannel(channelId);
			if (!channel || channel.guildId) return;
			void CallUtils.requestStartCall(
				i18n,
				channelId,
				CallUtils.getCallStartRequestOptions({shiftKey}, {kind: 'voice'}),
			);
		});
		this.register('chat_toggle_pins', ({type}) => {
			if (type !== 'press') return;
			ComponentDispatch.dispatch('CHANNEL_PINS_OPEN');
		});
		this.register('chat_toggle_inbox', ({type}) => {
			if (type !== 'press') return;
			ComponentDispatch.dispatch('INBOX_OPEN');
		});
		this.register('chat_toggle_member_list', ({type}) => {
			if (type !== 'press') return;
			ComponentDispatch.dispatch('CHANNEL_MEMBER_LIST_TOGGLE');
		});
		this.register('chat_toggle_emoji', ({type}) => {
			if (type !== 'press') return;
			const channelId = this.currentChannelId;
			if (!channelId) return;
			ComponentDispatch.dispatch('EXPRESSION_PICKER_TAB_TOGGLE', {channelId, tab: 'emojis'});
		});
		this.register('chat_toggle_gif', ({type}) => {
			if (type !== 'press') return;
			const channelId = this.currentChannelId;
			if (!channelId) return;
			ComponentDispatch.dispatch('EXPRESSION_PICKER_TAB_TOGGLE', {channelId, tab: 'gifs'});
		});
		this.register('chat_toggle_sticker', ({type}) => {
			if (type !== 'press') return;
			const channelId = this.currentChannelId;
			if (!channelId) return;
			ComponentDispatch.dispatch('EXPRESSION_PICKER_TAB_TOGGLE', {channelId, tab: 'stickers'});
		});
		this.register('chat_toggle_saved_media', ({type}) => {
			if (type !== 'press') return;
			const channelId = this.currentChannelId;
			if (!channelId) return;
			ComponentDispatch.dispatch('EXPRESSION_PICKER_TAB_TOGGLE', {channelId, tab: 'memes'});
		});
		this.register('nav_add_guild', ({type}) => {
			if (type !== 'press') return;
			ModalCommands.push(modal(() => React.createElement(AddGuildModal)));
		});
		this.register('chat_new_dm', ({type}) => {
			if (type !== 'press') return;
			ModalCommands.push(modal(() => React.createElement(CreateDMModal)));
		});
		this.register('chat_focus_textarea', ({type}) => {
			if (type !== 'press') return;
			const channelId = this.currentChannelId;
			if (!channelId) return;
			ComponentDispatch.dispatch('FOCUS_TEXTAREA', {channelId});
		});
		this.register('chat_upload', ({type}) => {
			if (type !== 'press') return;
			const channelId = this.currentChannelId;
			if (!channelId) return;
			ComponentDispatch.dispatch('TEXTAREA_UPLOAD_FILE', {channelId});
		});
		this.register('system_zoom_in', ({type}) => {
			if (type !== 'press') return;
			void Accessibility.adjustZoom(1);
		});
		this.register('system_zoom_out', ({type}) => {
			if (type !== 'press') return;
			void Accessibility.adjustZoom(-1);
		});
		this.register('system_zoom_reset', ({type}) => {
			if (type !== 'press') return;
			Accessibility.updateSettings({zoomLevel: 1.0});
		});
	}
	private resolveGlobalShortcutEventId(id: string):
		| (KeybindConfig & {
				combo: KeyCombo;
		  })
		| null {
		const currentKeybinds = this.activeKeybinds;
		const actionKeybind = currentKeybinds.find((keybind) => keybind.action === id);
		if (actionKeybind) return actionKeybind;
		const hookKeybind = currentKeybinds.find((keybind) => hookShortcutIdForKeybind(keybind) === id);
		if (hookKeybind) return hookKeybind;
		const portalAction = Keybind.getDefaultByAction(id as KeybindCommand);
		if (portalAction?.allowGlobal) return {...portalAction, combo: portalAction.combo};
		return this.activeGlobalKeybinds.find((keybind) => comboToShortcutString(keybind.combo) === id) ?? null;
	}
	private isHookShortcutRegistered(binding: RuntimeKeybind): boolean {
		const id = hookShortcutIdForKeybind(binding);
		return id !== null && this.hookFallbackShortcutIds.has(id);
	}
	private async refreshGlobalShortcuts() {
		const electronApi = getElectronAPI();
		if (!electronApi) return;
		const keybinds = this.activeGlobalKeybinds;
		const hasGlobalMouseShortcuts = this.activeMouseShortcutKeybinds.some(
			(k) => k.allowGlobal && (k.combo.global ?? false) === true,
		);
		let usingPortalShortcuts = false;
		if (!this.globalShortcutUnsubscribe) {
			this.globalShortcutUnsubscribe =
				electronApi.onGlobalShortcut?.((id: string) => {
					const keybind = this.resolveGlobalShortcutEventId(id);
					if (!keybind) return;
					const handler = this.handlers.get(keybind.action);
					if (!handler) return;
					if (this.suspended) return;
					if (Keybind.isActionMuted(keybind.action)) return;
					handler({
						type: 'press',
						source: 'global',
					});
				}) ?? null;
		}
		if (isNativeLinux() && electronApi.configureGlobalShortcutsPortal) {
			const desktopInfo = await electronApi.getDesktopInfo();
			if (desktopInfo.waylandSession) {
				const portalEntries = this.globalShortcutPortalEntries;
				this.ensureGlobalShortcutPortalBindingsSubscription();
				const portalConfigured = await electronApi.configureGlobalShortcutsPortal(portalEntries);
				if (portalConfigured) {
					usingPortalShortcuts = true;
					this.ensureGlobalKeybindTriggeredSubscription();
					for (const shortcut of new Set(this.registeredGlobalShortcutIds)) {
						try {
							await electronApi.unregisterGlobalShortcut?.(shortcut);
						} catch (error) {
							this.logger.error(`Failed to unregister Electron global shortcut ${shortcut}`, error);
						}
						this.registeredGlobalShortcutIds.delete(shortcut);
					}
					this.registeredPortalShortcutActionIds = new Set(portalEntries.map((entry) => entry.id));
					if (!hasGlobalMouseShortcuts) {
						for (const shortcut of new Set(this.hookFallbackShortcutIds)) {
							try {
								await electronApi.globalKeyHookUnregister?.(shortcut);
							} catch (error) {
								this.logger.error(`Failed to unregister fallback shortcut ${shortcut}`, error);
							}
							this.hookFallbackShortcutIds.delete(shortcut);
						}
						this.maybeStopGlobalKeyHook();
						return;
					}
				}
			}
		}
		if (!usingPortalShortcuts) {
			this.registeredPortalShortcutActionIds.clear();
			Keybind.setPortalShortcutBindings([]);
		}
		const desiredCombos = new Map<string, {combo: KeyCombo; hookOnly: boolean}>();
		if (!usingPortalShortcuts) {
			for (const k of keybinds) {
				const shortcut = comboToShortcutString(k.combo);
				if (shortcut && !desiredCombos.has(shortcut)) desiredCombos.set(shortcut, {combo: k.combo, hookOnly: false});
			}
		}
		for (const k of this.activeMouseShortcutKeybinds) {
			if (!k.allowGlobal || (k.combo.global ?? false) !== true) continue;
			const shortcut = hookShortcutIdForKeybind(k);
			if (shortcut && !desiredCombos.has(shortcut)) desiredCombos.set(shortcut, {combo: k.combo, hookOnly: true});
		}
		const desired = new Set(desiredCombos.keys());
		const previouslyRegistered = new Set(this.registeredGlobalShortcutIds);
		for (const shortcut of previouslyRegistered) {
			if (desired.has(shortcut)) continue;
			try {
				await electronApi.unregisterGlobalShortcut?.(shortcut);
			} catch (error) {
				this.logger.error(`Failed to unregister global shortcut ${shortcut}`, error);
			}
			this.registeredGlobalShortcutIds.delete(shortcut);
		}
		const previouslyHookRegistered = new Set(this.hookFallbackShortcutIds);
		for (const shortcut of previouslyHookRegistered) {
			if (desired.has(shortcut)) continue;
			try {
				await electronApi.globalKeyHookUnregister?.(shortcut);
			} catch (error) {
				this.logger.error(`Failed to unregister fallback shortcut ${shortcut}`, error);
			}
			this.hookFallbackShortcutIds.delete(shortcut);
		}
		for (const shortcut of desired) {
			if (this.registeredGlobalShortcutIds.has(shortcut)) continue;
			if (this.hookFallbackShortcutIds.has(shortcut)) continue;
			const desiredCombo = desiredCombos.get(shortcut);
			if (desiredCombo?.hookOnly) {
				if (await this.tryRegisterHookFallback(shortcut, desiredCombo.combo)) {
					this.hookFallbackShortcutIds.add(shortcut);
				}
				continue;
			}
			let osRegistered = false;
			try {
				osRegistered = (await electronApi.registerGlobalShortcut?.(shortcut, shortcut)) ?? false;
			} catch (error) {
				this.logger.error(`Failed to register global shortcut ${shortcut}`, error);
			}
			if (osRegistered) {
				this.registeredGlobalShortcutIds.add(shortcut);
				continue;
			}
			this.logger.warn(`Global shortcut did not register: ${shortcut}`);
			if (desiredCombo && (await this.tryRegisterHookFallback(shortcut, desiredCombo.combo))) {
				this.hookFallbackShortcutIds.add(shortcut);
			}
		}
	}
	private async tryRegisterHookFallback(shortcutId: string, combo: KeyCombo): Promise<boolean> {
		const electronApi = getElectronAPI();
		if (!electronApi?.globalKeyHookRegister) return false;
		const mouseButton = combo.mouseButton;
		const keycode = mouseButton == null ? jsKeyToUiohookKeycode(combo.code ?? combo.key) : null;
		if (mouseButton == null) {
			if (!isNativeLinux()) return false;
			if (keycode == null) return false;
		}
		const started = await this.startGlobalKeyHook();
		if (!started) return false;
		try {
			await electronApi.globalKeyHookRegister({
				id: shortcutId,
				...(keycode != null ? {keycode} : {}),
				...(mouseButton != null ? {mouseButton} : {}),
				ctrl: Boolean(combo.ctrl) || Boolean(combo.ctrlOrMeta),
				alt: Boolean(combo.alt),
				shift: Boolean(combo.shift),
				meta: Boolean(combo.meta),
			});
			return true;
		} catch (error) {
			this.logger.error(`Failed to register hook fallback for ${shortcutId}`, error);
			return false;
		}
	}
	private refreshLocalShortcuts() {
		if (!this.combokeys || this.suspended) return;
		this.combokeys.reset();
		this.detachLocalMouseShortcutListener();
		this.releaseGamepadShortcutStates();
		this.activeKeybinds.forEach((entry) => this.bindLocalShortcut(entry));
		this.refreshLocalMouseShortcutListener();
		this.refreshGamepadPolling();
	}
	private bindLocalShortcut(
		entry: KeybindConfig & {
			combo: KeyCombo;
		},
	) {
		const {combo, action} = entry;
		if (HOLD_ACTIONS.includes(action as HoldAction)) return;
		const requiresKeyboardMode = entry.requiresKeyboardMode ?? false;
		const requiresMessageFocus = entry.requiresMessageFocus ?? false;
		const handler = this.handlers.get(action);
		if (!handler) return;
		if (isFirefoxWebReservedZoomShortcut(action, combo)) return;
		const shortcuts = comboToCombokeysStrings(combo);
		if (shortcuts.length === 0) return;
		const hasModifier = !!(combo.ctrl || combo.ctrlOrMeta || combo.alt || combo.meta);
		const registeredGlobalShortcutId = comboToShortcutString(combo);
		const shouldIgnoreEvent = (event: KeyboardEvent): boolean => {
			const target = event.target ?? null;
			if (!isEditableElement(target)) return false;
			if (!hasModifier) return true;
			if (entry.ignoreWhileTyping) return true;
			return false;
		};
		const wrapHandler = (type: 'press' | 'release') => (event?: KeyboardEvent) => {
			if (!event) return;
			if (this.suspended) return;
			if (combo.code && event.code && combo.code !== event.code) return;
			if (shouldIgnoreEvent(event)) return;
			if (Keybind.isActionMuted(action)) return;
			if (
				(combo.global ?? false) &&
				((registeredGlobalShortcutId &&
					(this.registeredGlobalShortcutIds.has(registeredGlobalShortcutId) ||
						this.hookFallbackShortcutIds.has(registeredGlobalShortcutId))) ||
					this.registeredPortalShortcutActionIds.has(action))
			) {
				return;
			}
			if (requiresKeyboardMode && !KeyboardMode.keyboardModeEnabled) {
				return;
			}
			if (action === 'chat_focus_textarea' && KeyboardMode.keyboardModeEnabled) {
				return;
			}
			let focusedMessage: Message | null = null;
			if (requiresMessageFocus) {
				focusedMessage = MessageFocus.getFocusedMessage();
				if (!focusedMessage) {
					return;
				}
			}
			handler({
				type,
				source: 'local',
				context: focusedMessage ? {focusedMessage} : undefined,
				shiftKey: event.shiftKey,
			});
			if (type === 'press') event.preventDefault();
		};
		const combokeys = this.ensureCombokeys();
		if (combokeys) {
			combokeys.bind(shortcuts, wrapHandler('press'), 'keydown');
			combokeys.bind(shortcuts, wrapHandler('release'), 'keyup');
		}
	}
}
export default new KeybindManager();
